import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usersApi, getStaticUrl } from '../../api/client';
import './Profile.css';

const Profile = () => {
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const isFirstLogin = location.state?.firstLogin || false;
  const [formData, setFormData] = useState({
    username: '',
    real_name: '',
    age: '',
    mmr: '',
    profile_link: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        real_name: user.real_name || '',
        age: user.age || '',
        mmr: user.mmr || '',
        profile_link: user.profile_link || '',
      });
    }
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    // Validation du pseudo
    if (!formData.username || formData.username.trim().length < 3) {
      alert('Le pseudo doit contenir au moins 3 caractères');
      return;
    }

    // Validation des champs obligatoires pour la première connexion
    if (isFirstLogin) {
      if (!formData.profile_link || formData.profile_link.trim() === '') {
        alert('Le lien de profil est obligatoire pour créer votre profil');
        return;
      }
    }

    setSavingProfile(true);
    try {
      await usersApi.update(user.id, {
        username: formData.username.trim(),
        real_name: formData.real_name || null,
        age: formData.age ? parseInt(formData.age) : null,
        mmr: formData.mmr ? parseInt(formData.mmr) : null,
        profile_link: formData.profile_link || null,
      });

      // Rafraîchir les données utilisateur
      const { data } = await usersApi.getMe();
      refreshUser(data);
      alert('Profil mis à jour avec succès !');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setSavingPassword(true);
    try {
      await usersApi.updatePassword(user.id, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      alert('Mot de passe modifié avec succès !');
    } catch (err) {
      console.error('Erreur lors de la modification du mot de passe:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la modification du mot de passe');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximale : 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      await usersApi.uploadAvatar(user.id, file);
      const { data } = await usersApi.getMe();
      refreshUser(data);
      alert('Avatar mis à jour avec succès !');
    } catch (err) {
      console.error('Erreur lors de l\'upload de l\'avatar:', err);
      alert(err.response?.data?.detail || 'Erreur lors de l\'upload de l\'avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>Mon Profil</h1>
        <p className="profile-subtitle">Gérez vos informations personnelles</p>
      </div>

      {isFirstLogin && (
        <div className="first-login-banner">
          <h3>🎉 Bienvenue chez TSK !</h3>
          <p>
            Complétez votre profil pour commencer. Le champ <strong>Lien de profil</strong> est obligatoire.
          </p>
        </div>
      )}

      <div className="profile-content">
        {/* Section Avatar */}
        <div className="profile-section">
          <h2>Photo de profil</h2>
          <div className="avatar-section">
            <div className="avatar-preview">
              {user?.avatar ? (
                <img src={getStaticUrl(user.avatar)} alt={user.username} className="profile-avatar" />
              ) : (
                <div className="profile-avatar-placeholder">
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="avatar-actions">
              <label className="btn-upload-avatar">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={uploadingAvatar}
                  style={{ display: 'none' }}
                />
                {uploadingAvatar ? 'Envoi en cours...' : 'Changer l\'avatar'}
              </label>
              <p className="avatar-hint">JPG, PNG ou GIF. Taille max : 5MB</p>
            </div>
          </div>
        </div>

        {/* Section Informations personnelles */}
        <div className="profile-section">
          <h2>Informations personnelles</h2>
          <form onSubmit={handleProfileSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="username">Pseudo</label>
              <input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Entrez votre pseudo"
                minLength="3"
                required
              />
              <span className="field-hint">Minimum 3 caractères. Assurez-vous qu'il soit unique.</span>
            </div>

            <div className="form-group">
              <label htmlFor="real_name">Prénom / Nom</label>
              <input
                id="real_name"
                type="text"
                value={formData.real_name}
                onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
                placeholder="Entrez votre prénom ou nom complet"
              />
            </div>

            <div className="form-group">
              <label htmlFor="age">Âge</label>
              <input
                id="age"
                type="number"
                min="1"
                max="120"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                placeholder="Entrez votre âge"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mmr">MMR</label>
              <input
                id="mmr"
                type="number"
                min="0"
                value={formData.mmr}
                onChange={(e) => setFormData({ ...formData, mmr: e.target.value })}
                placeholder="Entrez votre MMR (ex: 1700)"
              />
              <span className="field-hint">Votre classement MMR</span>
            </div>

            <div className="form-group">
              <label htmlFor="profile_link">
                Lien de profil {isFirstLogin && <span className="required-badge">*</span>}
              </label>
              <input
                id="profile_link"
                type="url"
                value={formData.profile_link}
                onChange={(e) => setFormData({ ...formData, profile_link: e.target.value })}
                placeholder="https://tracker.gg/..."
                required={isFirstLogin}
              />
              <span className="field-hint">
                Lien vers votre tracker.gg, op.gg, etc. {isFirstLogin && '(Obligatoire)'}
              </span>
            </div>

            <button type="submit" className="btn-submit" disabled={savingProfile}>
              {savingProfile ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </form>
        </div>

        {/* Section Mot de passe */}
        <div className="profile-section">
          <h2>Modifier le mot de passe</h2>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Mot de passe actuel</label>
              <input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Entrez votre mot de passe actuel"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Entrez votre nouveau mot de passe"
                required
                minLength="6"
              />
              <span className="field-hint">Au moins 6 caractères</span>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirmez votre nouveau mot de passe"
                required
              />
            </div>

            <button type="submit" className="btn-submit" disabled={savingPassword}>
              {savingPassword ? 'Modification...' : 'Modifier le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
